import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, ArrowRight } from "lucide-react";
import { CATEGORY_BY_SLUG, SHEET_URL } from "../data/products";

const QuickView = ({ product, onClose }) => (
  <AnimatePresence>
    {product && (
      <motion.div
        key="backdrop"
        data-testid="product-quickview-backdrop"
        className="fixed inset-0 z-[80] flex items-center justify-center bg-night/80 p-4 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          key="panel"
          data-testid="product-quickview-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${product.name} quick view`}
          className="relative grid max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl md:grid-cols-2"
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            data-testid="product-quickview-close"
            onClick={onClose}
            aria-label="Close quick view"
            className="absolute right-4 top-4 z-10 rounded-full bg-night/80 p-2 text-white transition-colors hover:bg-teal-600"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="relative h-56 overflow-hidden bg-night md:h-full">
            <img src={product.image} alt={`${product.name} — ${product.tagline}`} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-night/70 via-transparent to-transparent md:bg-gradient-to-r" />
            <span className="absolute bottom-4 left-4 rounded-full border border-white/20 bg-night/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-200 backdrop-blur">
              {CATEGORY_BY_SLUG[product.category]}
            </span>
          </div>

          <div className="p-6 sm:p-8">
            <h3 className="font-display text-3xl font-extrabold tracking-tight text-night">{product.name}</h3>
            <p className="mt-1 text-sm font-medium text-teal-700">{product.tagline}</p>

            <dl className="mt-6 divide-y divide-slate-100 text-sm">
              {[
                ["Physical form", product.form],
                ["Species", product.species.join(", ")],
                ["Application / use", product.use],
                ["Inclusion rate", product.inclusion],
              ].map(([k, v]) => (
                <div key={k} className="spec-row flex items-start justify-between gap-6 py-3">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-500">{k}</dt>
                  <dd className="text-right font-medium text-slate-800">{v}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to={`/products/${product.slug}`}
                data-testid="product-quickview-full-details"
                onClick={onClose}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-night px-5 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-moss"
              >
                Full details <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <a
                href={SHEET_URL(product.slug)}
                data-testid="product-quickview-download-sheet"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-emerald-700 px-5 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-800 transition-colors hover:bg-emerald-50"
              >
                <Download className="h-3.5 w-3.5" /> Product sheet
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default QuickView;
